import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useDebounce } from "@/hooks/useDebounce";
import { useObjectives } from "@/hooks/useObjectives";
import { ObjectiveCategory, ObjectiveFilter } from "@/types";

export const OKR_ITEMS_PER_PAGE = 10;
export type OkrViewMode = "all" | "my";

/** Filter/page state for /okrs plus the memoised params that form the objectives query key. */
export function useOkrPageState() {
  const { user, canViewOkr } = useAuth();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [cyclePid, setCyclePid] = useState("");
  const [category, setCategory] = useState<ObjectiveCategory | "">("");
  const [viewMode, setViewMode] = useState<OkrViewMode>("all");
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, viewMode, cyclePid, category]);

  const params = useMemo<ObjectiveFilter>(() => ({
    search: debouncedSearch || undefined,
    statuses: statusFilter.length > 0 ? statusFilter : undefined,
    cyclePid: cyclePid || undefined,
    category: category || undefined,
    ownerUserPid: viewMode === "my" ? user?.pid : undefined,
    page,
    limit: OKR_ITEMS_PER_PAGE,
    sortBy: "orderIndex",
    sortOrder: "ASC",
  }), [debouncedSearch, statusFilter, cyclePid, category, viewMode, user?.pid, page]);

  const hasFilters =
    debouncedSearch !== "" || statusFilter.length > 0 || cyclePid !== "" || category !== "" || viewMode !== "all";

  const query = useObjectives(params, canViewOkr);

  return {
    search, setSearch,
    statusFilter, setStatusFilter,
    cyclePid, setCyclePid,
    category, setCategory,
    viewMode, setViewMode,
    page, setPage,
    params, hasFilters, query,
  };
}
